import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { CheckCircle2, ArrowRight, ShieldCheck, PhoneCall, Building2, Briefcase } from "lucide-react";

export default function PublicPricing() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/posting/products").then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load posting membership"));
  }, []);

  if (!data) return <div className="min-h-screen bg-white flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  const memberships = (data.products || []).filter(p => p.type === "MEMBERSHIP");
  const single = (data.products || []).find(p => p.type === "SINGLE");
  const addon = (data.products || []).find(p => p.type === "ADDON");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100 px-8 py-4 flex items-center justify-between">
        <Link to="/" className="font-display font-bold text-lg tracking-tight">Connect<span className="text-teal-300">My</span>Hub<sup className="text-xs">™</sup></Link>
        <nav className="flex items-center gap-6 text-xs font-semibold">
          <Link to="/employer-portal/signup" data-testid="nav-signup" className="hover:text-white">Employer signup</Link>
          <Link to="/login" className="px-3 py-1.5 rounded bg-white text-slate-900">Sign in</Link>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center mb-10">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-2">Employer posting membership</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900">Post your jobs on ConnectMyHub™</h1>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Choose a posting membership and keep a set number of jobs live at once. Memberships are billed monthly on a 12-month contract.</p>
        </div>

        {/* VERIFICATION NOTICE — exact brief wording */}
        <div data-testid="verification-notice" className="mb-8 rounded-xl border-2 border-sky-300 bg-sky-50 px-6 py-4 flex items-center gap-3 justify-center">
          <PhoneCall className="text-sky-600 shrink-0" size={20}/>
          <div className="text-sm font-semibold text-sky-900">{data.verification_notice}</div>
        </div>

        {/* POSTING MEMBERSHIP TIERS */}
        <div className="grid md:grid-cols-3 gap-4">
          {memberships.map((p, i) => (
            <div key={p.code} data-testid={`posting-plan-${p.code}`} className={`bg-white rounded-xl border-2 ${i === 1 ? "border-slate-900 shadow-lg" : "border-slate-200"} p-6 flex flex-col`}>
              {i === 1 && <div className="text-[10px] font-bold uppercase tracking-widest text-slate-900 mb-2">Most Popular</div>}
              <div className="text-lg font-display font-bold text-slate-900">{p.name.replace("ConnectMyHub Posting Membership — ", "")}</div>
              <div className="mt-3 mb-1">
                <span className="font-display text-3xl font-bold text-slate-900" data-testid={`posting-price-${p.code}`}>${p.price.toLocaleString()}</span>
                <span className="text-sm text-slate-500">/mo</span>
              </div>
              <div className="text-xs text-slate-500 mb-4" data-testid={`posting-billing-${p.code}`}>{p.billing}</div>
              <div className="text-sm text-slate-700 space-y-1.5 mb-6 flex-1">
                <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600 shrink-0"/> <b>{p.postings_cap}</b> live postings at any one time</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600 shrink-0"/> Each posting runs ~30 days</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600 shrink-0"/> Closing or filling a job frees its slot</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600 shrink-0"/> Extra slots available (+${addon?.price}/mo each)</div>
              </div>
              <Link to="/employer-portal/signup" data-testid={`posting-cta-${p.code}`}
                    className="h-11 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-1 hover:bg-slate-800">
                Get started <ArrowRight size={14}/>
              </Link>
            </div>
          ))}
        </div>

        {/* SINGLE + ADD-ON */}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {single && (
            <div className="bg-white rounded-xl border-2 border-slate-200 p-6 flex items-center justify-between gap-4" data-testid={`posting-plan-${single.code}`}>
              <div>
                <div className="font-display text-xl font-bold text-slate-900">Single Job Posting</div>
                <div className="text-xs text-slate-500 mt-0.5">{single.billing}</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-2" data-testid={`posting-price-${single.code}`}>${single.price}</div>
              </div>
              <Link to="/employer-portal/signup" data-testid={`posting-cta-${single.code}`} className="h-11 px-5 rounded-lg border-2 border-slate-900 text-slate-900 text-sm font-semibold inline-flex items-center gap-1">Post one job <ArrowRight size={14}/></Link>
            </div>
          )}
          {addon && (
            <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-6 flex items-center gap-3" data-testid={`posting-plan-${addon.code}`}>
              <Briefcase className="text-slate-500 shrink-0" size={22}/>
              <div className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Extra Job Slot — ${addon.price}/mo.</span> Add concurrent posting slots to any membership when you hit your cap.</div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-500 mt-6" data-testid="no-free-postings-note">ConnectMyHub does not offer free public job postings. Payment is collected only after your verification call clears.</p>

        {/* DIRECT HIRE — standalone pay-per-hire service (NOT a membership) */}
        <section className="mt-14 rounded-2xl border border-slate-200 bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="direct-hire-service">
          <div className="max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-700">Direct Hire · pay-per-hire service</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl mt-2 text-slate-900">Want us to recruit and place the hire for you?</h2>
            <p className="text-slate-600 text-sm mt-3">Our Direct Hire service sources, screens and places candidates on a pay-per-hire basis — a <b>30% placement fee</b> with our standard placement guarantee. Candidate fees are always $0. This is a service, not a membership.</p>
          </div>
          <Link to="/employer/workforce-assessment" data-testid="direct-hire-cta"
                className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-md border-2 border-slate-900 text-slate-900 text-xs font-semibold uppercase tracking-widest hover:bg-slate-900 hover:text-white">
            Talk to ConnectMyHub <ArrowRight size={14}/>
          </Link>
        </section>

        {/* AI WORKFORCE */}
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="employer-ai-workforce-section">
          <div className="max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-700">AI Workforce · Hire AI Employees</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl mt-2 text-slate-900">Add AI Employees alongside your human hires</h2>
            <p className="text-slate-600 text-sm mt-3">Specialized AI Employees and AI Departments for sales, marketing, operations, revenue and CRM — on a monthly membership, available nationwide.</p>
          </div>
          <Link to="/workforce" data-testid="employer-ai-workforce-cta"
                className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-md bg-slate-900 text-white text-xs font-semibold uppercase tracking-widest hover:bg-slate-800">
            Hire AI Employees <ArrowRight size={14}/>
          </Link>
        </section>

        {/* ADVERTISE */}
        <section className="mt-6 rounded-2xl bg-slate-950 text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="employer-advertise-section">
          <div className="max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-300">Advertise on ConnectMyHub</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl mt-2">Promote your business to the ConnectMyHub audience</h2>
            <p className="text-slate-300 text-sm mt-3">Sponsored placements put your brand in front of employers, professionals and job seekers. Spotlight from $149/month through Market, County, Regional and National sponsorships. Professional ad creative and one revision included.</p>
          </div>
          <Link to="/advertise" data-testid="employer-advertise-cta"
                className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-md bg-teal-500 text-slate-950 text-xs font-semibold uppercase tracking-widest hover:bg-teal-400">
            View advertising packages <ArrowRight size={14}/>
          </Link>
        </section>

        <footer className="mt-16 pt-8 border-t border-slate-200 text-center text-xs text-slate-500">
          <div className="inline-flex items-center gap-2"><ShieldCheck size={13}/> ConnectMyHub™ · People. Skills. Business. Connected.™ · by My Pipeline Solutions · Candidate fees are always $0.</div>
        </footer>
      </main>
      <Toaster position="top-right" richColors/>
    </div>
  );
}
